import type { MailboxDomain, MailboxError } from '../../mock-data/mailbox-records';

/**
 * What a create ANSWERS after the operator left the section, held for their return, IN MEMORY ONLY
 * (the same reasoning as `firstRun.ts`: a module-level map dies with the tab and is never written
 * anywhere). Both outcomes, built to the same rules:
 *
 *  - a REFUSAL (a 409, a 400, the timeout): the section reopens the form exactly as it was left,
 *    with the refusal where it renders;
 *  - a SUCCESS: the section re-reads the list (the row on screen is the service's, never one made
 *    from the held answer) and opens the new mailbox's page, where the first-run password shows once
 *    (the password itself stays in `firstRun.ts`; nothing here holds it).
 *
 * Why it exists: the setup screen says "You can leave this page; setup continues without you" from
 * its first frame. An answer that lands once the section has unmounted would otherwise go nowhere:
 * a refusal with no reason, and, worse, a success the grid does not know about, so it offers
 * "Create free mailbox" again and the second create is charged as `paid`.
 *
 * Keyed by BRAND and domain (a success also by its address), so another brand's section never sees
 * it, even where two brands share the mock's fallback domain. Taken once. Any newer create on the
 * same key drops whatever is held: what the operator is doing now is what matters. A section already
 * on screen when an answer lands (the operator came back before the create answered) hears it
 * through {@link onHeldChange}, so it is shown then, not on some later visit.
 *
 * THE DOUBLE-CHARGE GUARD: while a create for the FREE slot is in flight, or its success is held and
 * not yet taken, {@link freeSlotBusy} is true and the grid must not offer the free create again.
 */
export type HeldRefusal = {
  draft: { email: string; password: string; typed: boolean };
  error: MailboxError | null;
};

/** A create that succeeded while nobody was looking: enough for the section to bring the row in
 *  (live: re-read; mock, which has no service: the create dispatch a normal create uses) and open
 *  the mailbox's page. */
export type HeldSuccess = {
  email: string;
  billing: 'free' | 'paid';
  invoiceId?: string;
  mailDomain?: MailboxDomain;
  /** What the create did; more than `mailbox_created` means it set the domain up. */
  steps: string[];
};

const refusals = new Map<string, HeldRefusal>();
/** Keyed by brand + domain + address. */
const successes = new Map<string, HeldSuccess>();
const listeners = new Map<string, Set<() => void>>();
/** Creates started per key: an answer is held only for the LATEST one, so an abandoned attempt's
 *  answer can never surface after a newer create. */
const started = new Map<string, number>();
/** The attempt number of a free-slot create still in flight, per key. */
const freeInFlight = new Map<string, number>();

const keyOf = (brandId: string | undefined, domain: string): string => `${brandId ?? ''}|${domain.trim().toLowerCase()}`;
const successKey = (key: string, email: string): string => `${key}|${email.trim().toLowerCase()}`;
const isUnder = (key: string, k: string): boolean => k.startsWith(`${key}|`);

const notify = (key: string): void => {
  listeners.get(key)?.forEach((listener) => listener());
};

/** Drop every success held for `key` (any address). */
const dropSuccesses = (key: string): void => {
  for (const k of Array.from(successes.keys())) {
    if (isUnder(key, k)) successes.delete(k);
  }
};

/**
 * A create is starting on the brand's `domain`: returns its attempt number, for its answer. Anything
 * still held for that key is dropped (the newer create's own answer is the one that matters). A
 * `freeSlot` create is tracked as in flight until {@link settleCreate}, for the guard.
 */
export const markCreateStarted = (
  brandId: string | undefined,
  domain: string,
  { freeSlot = false }: { freeSlot?: boolean } = {},
): number => {
  const key = keyOf(brandId, domain);
  const attempt = (started.get(key) ?? 0) + 1;
  started.set(key, attempt);
  refusals.delete(key);
  dropSuccesses(key);
  // Only settleCreate clears the in-flight marker: a paid create starting on the same key must not
  // lift the guard while a free one is still out.
  if (freeSlot) freeInFlight.set(key, attempt);
  notify(key);
  return attempt;
};

/** The create `attempt` has answered (either way): it is no longer in flight. */
export const settleCreate = (brandId: string | undefined, domain: string, attempt: number): void => {
  const key = keyOf(brandId, domain);
  if (freeInFlight.get(key) !== attempt) return;
  freeInFlight.delete(key);
  notify(key);
};

/** Hold a refusal that arrived after the operator left. Ignored when a newer create has started
 *  since `attempt`. */
export const holdRefusal = (
  brandId: string | undefined,
  domain: string,
  attempt: number,
  refusal: HeldRefusal,
): void => {
  const key = keyOf(brandId, domain);
  if (attempt < (started.get(key) ?? 0)) return;
  refusals.set(key, refusal);
  notify(key);
};

/** Hold a success that arrived after the operator left. Ignored when a newer create has started
 *  since `attempt`. */
export const holdSuccess = (
  brandId: string | undefined,
  domain: string,
  attempt: number,
  success: HeldSuccess,
): void => {
  const key = keyOf(brandId, domain);
  if (attempt < (started.get(key) ?? 0)) return;
  successes.set(successKey(key, success.email), success);
  notify(key);
};

/** Listen for a create starting, settling, or an answer being held for the brand's `domain` while its
 *  section is mounted (taking one does not notify: the taker re-renders itself). Returns the
 *  unsubscribe, for the effect's cleanup. */
export const onHeldChange = (brandId: string | undefined, domain: string, listener: () => void): (() => void) => {
  const key = keyOf(brandId, domain);
  const set = listeners.get(key) ?? new Set<() => void>();
  set.add(listener);
  listeners.set(key, set);
  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(key);
  };
};

/** Take (and forget) the refusal held for the brand's `domain`, or `null` when there is none. */
export const takeRefusal = (brandId: string | undefined, domain: string): HeldRefusal | null => {
  const key = keyOf(brandId, domain);
  const refusal = refusals.get(key) ?? null;
  refusals.delete(key);
  return refusal;
};

/** Take (and forget) a success held for the brand's `domain`, or `null` when there is none. */
export const takeSuccess = (brandId: string | undefined, domain: string): HeldSuccess | null => {
  const key = keyOf(brandId, domain);
  for (const [k, success] of Array.from(successes.entries())) {
    if (isUnder(key, k)) {
      successes.delete(k);
      return success;
    }
  }
  return null;
};

/** Whether the free slot is spoken for: a free-slot create in flight, or a free success held and
 *  not yet taken. While true, the grid must not offer "Create free mailbox". */
export const freeSlotBusy = (brandId: string | undefined, domain: string): boolean => {
  const key = keyOf(brandId, domain);
  if (freeInFlight.has(key)) return true;
  for (const [k, success] of Array.from(successes.entries())) {
    if (isUnder(key, k) && success.billing === 'free') return true;
  }
  return false;
};
